/**
 * Analyzer: Setup × ATM Fit
 *
 * For a given setup, walks each ATM strategy that's been used on this
 * setup's trades and asks: "which management style fits this setup
 * best?" Same shape as setupConfluenceFit but the actionable question is
 * different — there's no "pin ATM to setup" data model (ATM is picked
 * per-trade), so this analyzer is informational.
 *
 * Three patterns:
 *
 *   1. WINNER: One ATM dominates the field with meaningful sample. Tells
 *      the trader to default to that ATM when this setup fires. SUCCESS.
 *
 *   2. LOSER: One ATM is meaningfully worse than the average across this
 *      setup's trades — flags it as a regime mismatch. WARNING.
 *
 *   3. SOLO: Only one ATM has ever been tried. We don't have signal — but
 *      surface a hint that experimenting with alternates is informative.
 *      INFO. Low priority.
 *
 * Reads from ctx:
 *   setupName, setupTrades         — provided by setup scope
 *   atmDetails                     — ATM template configurations (for richer descriptions)
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';

export const key = 'setupAtmFit';
export const tier = 'free';

// Need at least this many trades on a single ATM before we trust its WR.
const MIN_TRADES_PER_ATM = 4;
// Difference vs the setup's overall WR before flagging an ATM as winner/loser.
const MIN_WR_DELTA_VS_BASELINE = 0.20;

const fmtPct = (n) => `${Math.round(n * 100)}%`;

const computeAtmFit = (setupTrades) => {
  // Group trades by atmStrategy (skip trades without one).
  const byAtm = new Map();
  for (const t of setupTrades) {
    const atm = (t?.atmStrategy || '').trim();
    if (!atm) continue;
    if (!byAtm.has(atm)) byAtm.set(atm, []);
    byAtm.get(atm).push(t);
  }
  if (byAtm.size === 0) return null;

  // Setup baseline WR (across trades that did have an ATM tagged).
  const tradesWithAtm = [...byAtm.values()].flat();
  if (tradesWithAtm.length === 0) return null;
  const baselineWR = tradesWithAtm.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length / tradesWithAtm.length;

  const wr = (group) => group.length === 0 ? 0
    : group.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length / group.length;

  const stats = [];
  for (const [atmName, trades] of byAtm.entries()) {
    if (trades.length < MIN_TRADES_PER_ATM) continue;
    const atmWR = wr(trades);
    stats.push({
      atmName,
      count: trades.length,
      wr: atmWR,
      delta: atmWR - baselineWR,
    });
  }

  return {
    atmCount: byAtm.size,
    baselineWR,
    baselineCount: tradesWithAtm.length,
    stats: stats.sort((a, b) => b.wr - a.wr),
  };
};

// ── Public analyze() ──────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { setupName, setupTrades } = ctx || {};
  if (!setupName || !Array.isArray(setupTrades) || setupTrades.length === 0) return null;

  const fit = computeAtmFit(setupTrades);
  if (!fit) return null;

  const { atmCount, baselineWR, stats } = fit;

  // Solo case: only one ATM ever tried for this setup. Hint without claiming signal.
  if (atmCount === 1) {
    const only = [...new Set(setupTrades.map(t => t.atmStrategy).filter(Boolean))];
    if (only.length === 1) {
      return makeObservation({
        id: `${key}:${setupName}:solo`,
        analyzer: key,
        scope: 'setup',
        scopeId: setupName,
        severity: SEVERITY.INFO,
        category: CATEGORY.SETUP,
        title: `"${setupName}" has only been run with "${only[0]}"`,
        detail: `No comparison data — every trade on this setup used the same ATM. Hard to know whether the management style is the edge or just the default.`,
        action: `Try one or two trades with a different ATM (wider stop, different trail) to see whether "${only[0]}" is genuinely the right fit or just the habit.`,
        evidence: { atm: only[0] },
        confidence: 0.5,
        priority: 40,
      });
    }
  }

  if (stats.length === 0) return null;

  const observations = [];
  const top = stats[0];
  const bottom = stats[stats.length - 1];

  // WINNER pattern — one ATM well above baseline with adequate sample.
  if (top.delta >= MIN_WR_DELTA_VS_BASELINE) {
    observations.push(makeObservation({
      id: `${key}:${setupName}:winner:${top.atmName}`,
      analyzer: key,
      scope: 'setup',
      scopeId: setupName,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.SETUP,
      title: `"${top.atmName}" is the right ATM for "${setupName}" — ${fmtPct(top.wr)} WR`,
      detail: `${top.count} trades on "${top.atmName}": ${fmtPct(top.wr)} WR (${fmtPct(top.delta)} above the setup's ${fmtPct(baselineWR)} baseline). Other ATMs trail.`,
      action: `Default to "${top.atmName}" when "${setupName}" fires. Note the bracket/trail config in this setup's rules.`,
      evidence: { atmName: top.atmName, wr: top.wr, baselineWR, delta: top.delta, count: top.count },
      confidence: 0.84,
      priority: 78,
    }));
  }

  // LOSER pattern — one ATM well below baseline. Different from winner —
  // we WARN about a specific ATM being a drag, even when the winner isn't
  // strongly above the average (because removing the loser still helps).
  if (stats.length >= 2 && bottom.atmName !== top.atmName && bottom.delta <= -MIN_WR_DELTA_VS_BASELINE) {
    observations.push(makeObservation({
      id: `${key}:${setupName}:loser:${bottom.atmName}`,
      analyzer: key,
      scope: 'setup',
      scopeId: setupName,
      severity: SEVERITY.WARNING,
      category: CATEGORY.SETUP,
      title: `"${bottom.atmName}" is misfiring on "${setupName}" — ${fmtPct(bottom.wr)} WR`,
      detail: `${bottom.count} trades on "${bottom.atmName}": ${fmtPct(bottom.wr)} WR (${fmtPct(Math.abs(bottom.delta))} below the setup's ${fmtPct(baselineWR)} baseline).`,
      action: `Stop using "${bottom.atmName}" on "${setupName}" trades — the bracket/trail config is fighting the setup's natural rhythm.`,
      evidence: { atmName: bottom.atmName, wr: bottom.wr, baselineWR, delta: bottom.delta, count: bottom.count },
      confidence: 0.78,
      priority: 76,
    }));
  }

  return observations.length === 0 ? null : observations;
};

// Exports for tests
export { computeAtmFit };
